import {
  CompensationMethodData,
  getEligibleExchangeGroupsForProduct,
  getGroupedReturnOptionsButtonText,
  getReturnStepPageOptions,
  isEnsoEngraving,
  ItemReturnFlowStep,
  ReturnOptionType,
  StepType,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExchangeVariant,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { getMultipleChoiceAnswers } from "@redotech/redo-model/draft-return/util";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { ReturnFlow, Step } from "@redotech/redo-model/return-flow";
import { ExchangeGroup } from "@redotech/redo-model/team";
import {
  Button,
  ButtonBorder,
  ButtonSize,
  ButtonTheme,
} from "@redotech/redo-web/button";
import BundlePartsIcon from "@redotech/redo-web/icon-old/bundle-parts.svg";
import ChevronDown from "@redotech/redo-web/icon-old/chevron-down.svg";
import RotateIcon from "@redotech/redo-web/icon-old/rotate-right-dash-1--streamline-core.svg";
import CartIcon from "@redotech/redo-web/icon-old/shopping-cart-1.svg";
import ReloadArrowIcon from "@redotech/redo-web/icon-old/triangle-arrow-reload.svg";
import { memo, useEffect, useMemo } from "react";
import { BundleType } from "./bundle-type";
import { EnsoEngravingFee } from "./enso-engraving";
import { ExchangeGroupExchange } from "./exchange-group-exchange";
import * as multipleChoiceQuestionCss from "./multiple-choice-question.module.css";
import * as productCss from "./product.module.css";
import * as returnOptionsCss from "./return-options.module.css";
import { ExchangeProductVariant } from "./variant-bubbles";
import { VariantExchange } from "./variant-exchange";
import { VariantExchangeFooter } from "./variant-exchange-footer";
export interface ExchangeItem {
  variant: ExchangeVariant;
  exchangeGroup?: ExchangeGroup;
  variantExchange: boolean;
  copyProperties: boolean;
}

export const ReturnStep = memo(function ReturnStep({
  returnStep,
  product,
  settings,
  onBack,
  submitItem,
  onVariantVisible,
  step,
  pushReturnMethodStep,
  selectedReturnItem,
  returnType,
  flow,
  hasStorefrontAccess,
}: {
  returnStep: Step.Return;
  product: ReturnProduct;
  settings: ReturnAppSettings;
  step: ItemReturnFlowStep;
  onBack: () => void;
  onVariantVisible: (variant: ExchangeVariant | undefined) => void;
  pushReturnMethodStep: (step: ItemReturnFlowStep) => void;
  submitItem: (data: CompensationMethodData) => void;
  selectedReturnItem: ReturnableItem;
  returnType?: ReturnTypeEnum;
  flow: ReturnFlow;
  hasStorefrontAccess: boolean;
}) {
  const eligibleExchangeGroups = useMemo(
    () => getEligibleExchangeGroupsForProduct(product, settings.exchangeGroups),
    [product, settings.exchangeGroups],
  );

  const options = useMemo(() => {
    const options: { name: string; next(): void; icon: JSX.Element }[] = [];
    const returnStepOptions = getReturnStepPageOptions(
      returnStep,
      product,
      settings,
      selectedReturnItem,
      returnType,
    );

    for (const returnStepOption of returnStepOptions) {
      switch (returnStepOption.type) {
        case ReturnOptionType.SAME_ITEM_EXCHANGE:
          options.push({
            name: settings.sameItemTitle || "Exchange for same item",
            next: () => {
              if (
                isEnsoEngraving(
                  settings,
                  product,
                  returnType || ReturnTypeEnum.CLAIM,
                )
              ) {
                pushReturnMethodStep({
                  type: StepType.ENSO_ENGRAVING,
                  flowPath: step.flowPath,
                  inputAnswers: step.inputAnswers,
                  multipleChoiceAnswers: step.multipleChoiceAnswers,
                  variant: returnStepOption.variant,
                });
              } else {
                submitItem({
                  variantExchangeItem: {
                    variantId: returnStepOption.variant.id,
                    preserveProperties: true,
                    image: returnStepOption.variant.image,
                    title: returnStepOption.variant.title,
                  },
                });
              }
            },
            icon: <RotateIcon className={returnOptionsCss.icon} />,
          });
          break;
        case ReturnOptionType.VARIANT_EXCHANGE:
          options.push({
            name:
              settings.variantExchangeTitle || "Exchange for a new color/size",
            next() {
              pushReturnMethodStep({
                type: StepType.VARIANT_EXCHANGE,
                flowPath: step.flowPath,
                inputAnswers: step.inputAnswers,
                multipleChoiceAnswers: step.multipleChoiceAnswers,
              });
            },
            icon: <RotateIcon className={returnOptionsCss.icon} />,
          });
          break;
        case ReturnOptionType.EXCHANGE_GROUPS:
          if (!hasStorefrontAccess) {
            break;
          }
          options.push({
            name:
              eligibleExchangeGroups.length === 1
                ? eligibleExchangeGroups[0].groupName
                : "Exchange for a similar item",
            next() {
              if (eligibleExchangeGroups.length === 1) {
                pushReturnMethodStep({
                  type: StepType.EXCHANGE_GROUP_SELECTED,
                  exchangeGroup: eligibleExchangeGroups[0],
                  flowPath: step.flowPath,
                  inputAnswers: step.inputAnswers,
                  multipleChoiceAnswers: step.multipleChoiceAnswers,
                });
              } else {
                pushReturnMethodStep({
                  type: StepType.EXCHANGE_GROUP_SELECTION,
                  flowPath: step.flowPath,
                  inputAnswers: step.inputAnswers,
                  multipleChoiceAnswers: step.multipleChoiceAnswers,
                });
              }
            },
            icon: <ReloadArrowIcon className={returnOptionsCss.icon} />,
          });
          break;
        case ReturnOptionType.SHOP_OR_RETURN:
          options.push({
            name:
              settings.theme.notExchangeButtonText ||
              getGroupedReturnOptionsButtonText(returnStepOption.options).text,
            next() {
              submitItem({});
            },
            icon: <CartIcon className={returnOptionsCss.icon} />,
          });
          break;
        case ReturnOptionType.BUNDLE_TYPE:
          options.push({
            name: "Choose how you want to return your bundle",
            next() {
              pushReturnMethodStep({
                type: StepType.BUNDLE_TYPE,
                options: returnStepOption.options,
                flowPath: step.flowPath,
                inputAnswers: step.inputAnswers,
                multipleChoiceAnswers: step.multipleChoiceAnswers,
              });
            },
            icon: <BundlePartsIcon />,
          });
          break;
      }
    }

    return options;
  }, [
    eligibleExchangeGroups,
    product,
    pushReturnMethodStep,
    settings,
    returnStep,
    step,
    submitItem,
    selectedReturnItem,
    hasStorefrontAccess,
    returnType,
  ]);

  // if you include options here we get an infinite rendering loop
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (options.length === 1) {
      options[0].next();
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const chooseVariant = (variant: ExchangeVariant) => {
    submitItem({
      variantExchangeItem: {
        variantId: variant.id,
        preserveProperties: true,
        title: variant.title,
        image: variant.image,
      },
    });
  };

  switch (step.type) {
    case "flow": {
      return (
        <div className={multipleChoiceQuestionCss.wrapper}>
          <div className={multipleChoiceQuestionCss.grid}>
            {options.map((question, index) => (
              <Button
                border={ButtonBorder.LIGHT}
                chevron
                key={index}
                onClick={question.next}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.SOLID_LIGHT}
              >
                <div className={returnOptionsCss.optionButton}>
                  <div className={returnOptionsCss.iconcontainer}>
                    {question.icon}
                  </div>
                  <div className={returnOptionsCss.optionText}>
                    {question.name}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>
      );
    }
    case "variant-exchange": {
      return (
        <VariantExchange
          chooseVariant={chooseVariant}
          footer={VariantExchangeFooter}
          multipleChoiceAnswers={getMultipleChoiceAnswers(
            step.multipleChoiceAnswers,
            flow,
            step.flowPath,
          ).map((answer) => answer.answer)}
          onBack={onBack}
          onVariantVisible={onVariantVisible}
          product={product}
          returnType={returnType}
          settings={settings}
          submitItem={submitItem}
        />
      );
    }
    case "exchange-group-selection": {
      return (
        <div className={multipleChoiceQuestionCss.wrapper}>
          <div className={productCss.questionHeader}>
            Choose one of the following categories
          </div>
          <div className={multipleChoiceQuestionCss.grid}>
            {eligibleExchangeGroups.map((group, index) => (
              <Button
                border={ButtonBorder.LIGHT}
                key={index}
                onClick={() =>
                  pushReturnMethodStep({
                    type: StepType.EXCHANGE_GROUP_SELECTED,
                    exchangeGroup: group,
                    flowPath: step.flowPath,
                    inputAnswers: step.inputAnswers,
                    multipleChoiceAnswers: step.multipleChoiceAnswers,
                  })
                }
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.OUTLINED}
              >
                <div className={returnOptionsCss.optionButton}>
                  <div className={returnOptionsCss.optionText}>
                    {group.groupName}
                  </div>
                  <ChevronDown className={returnOptionsCss.chevron} />
                </div>
              </Button>
            ))}
          </div>
        </div>
      );
    }
    case "exchange-group-selected": {
      return (
        <ExchangeGroupExchange
          exchangeGroup={step.exchangeGroup}
          onItemSelected={(variant: ExchangeProductVariant | null) => {
            if (!variant) {
              return;
            }
            submitItem({
              variantExchangeItem: {
                variantId: variant.id,
                preserveProperties: false,
                title: variant.title,
                image: variant.image,
                productTitle: variant.productTitle,
                productId: variant.productId,
              },
              exchangeGroup: step.exchangeGroup,
            });
          }}
          returnType={returnType}
          selectedReturnItem={selectedReturnItem}
          settings={settings}
        />
      );
    }
    case "bundle-type": {
      return (
        <BundleType
          availableReturnOptions={step.options}
          chooseVariant={chooseVariant}
          onSubmit={submitItem}
          returnableItem={selectedReturnItem}
          settings={settings}
        />
      );
    }
    case "enso-engraving": {
      return (
        <EnsoEngravingFee
          multipleChoiceAnswers={getMultipleChoiceAnswers(
            step.multipleChoiceAnswers,
            flow,
            step.flowPath,
          ).map((answer) => answer.answer)}
          returnType={returnType}
          selectedVariant={step.variant}
          submitItem={submitItem}
        />
      );
    }
  }
  return null;
});
